import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { executeOrNull, Guid, Nillable, valueOrDefault } from '@web/utils';
import {
  PublicIssueComplaintForm,
  PublicIssueForm,
  PublicIssueFormGroup,
} from '../../models/public-issue-form';
import {
  ArrayValidator,
  CheckboxWithLabelComponent,
  EmailValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengMultiselectDirective,
  PrimengSelectDirective,
  PrimengTextareaDirective,
  ReCaptchaComponent,
  SelectItem,
} from '@web/shared/ui';
import { InputText } from 'primeng/inputtext';
import { PublicIssueTypeProviderService } from '../../providers/public-issue-type-provider.service';
import { map, Observable, startWith, tap } from 'rxjs';
import { PublicIssueType } from '../../enums/public-issue-type';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { PublicIssueComplaintType } from '../../enums/public-issue-complaint-type';
import { PublicIssueComplaintTypeProviderService } from '../../providers/public-issue-complaint-type-provider.service';
import { MultiSelect } from 'primeng/multiselect';
import { PublicShopMultimediaUploadControlComponent } from '@web/shared/module/multimedia';
import { RecaptchaModule } from 'ng-recaptcha';

@Component({
  selector: 'app-public-contact-form',
  imports: [
    Button,
    Checkbox,
    CheckboxWithLabelComponent,
    CommonModule,
    FormControlComponent,
    FormsModule,
    InputText,
    MultiSelect,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengMultiselectDirective,
    PrimengSelectDirective,
    PrimengTextareaDirective,
    ReactiveFormsModule,
    Select,
    Textarea,
    PublicShopMultimediaUploadControlComponent,
    RecaptchaModule,
    ReCaptchaComponent,
  ],
  templateUrl: './public-issue-form.component.html',
  styleUrl: './public-issue-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicIssueFormComponent {
  private readonly builder = inject(FormBuilder);
  private readonly issueTypeProvider = inject(PublicIssueTypeProviderService);
  /* prettier-ignore */
  private readonly issueComplaintTypeProvider = inject(PublicIssueComplaintTypeProviderService);

  public readonly data = input<Nillable<PublicIssueForm>>();
  public readonly submitSuccessEvent = output<PublicIssueForm>();

  /* prettier-ignore */
  protected readonly issueTypeSelectItems$: Observable<SelectItem<PublicIssueType>[]>;
  /* prettier-ignore */
  protected readonly issueComplaintTypeSelectItems$: Observable<SelectItem<PublicIssueComplaintType>[]>;
  /* prettier-ignore */
  protected readonly complaintFormGroupAvailability$: Observable<boolean>;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly form!: PublicIssueFormGroup;
  protected readonly maxFileSize = 10 * 1000 * 1000;
  protected readonly maxNumberOfFiles = 5;

  public constructor() {
    this.form = this.buildForm();
    /* prettier-ignore */
    this.issueTypeSelectItems$ = this.issueTypeSelectItemsSource();
    /* prettier-ignore */
    this.issueComplaintTypeSelectItems$ = this.issueComplaintTypeSelectItemsSource();
    /* prettier-ignore */
    this.complaintFormGroupAvailability$ = this.prepareComplaintFormGroupAvailabilitySource();
  }

  public handleSubmitClick(): void {
    const data = this.form.value;

    this.submitSuccessEvent.emit(
      new PublicIssueForm({
        type: data.type as PublicIssueType,
        complaint: executeOrNull(
          data.complaint,
          (value) =>
            new PublicIssueComplaintForm({
              fileIds: value.fileIds as Guid[],
              orderNumber: value.orderNumber as string,
              reasons: value.reasons as PublicIssueComplaintType[],
            }),
        ),
        acceptRules: data.acceptRules as boolean,
        dignity: data.dignity as string,
        email: data.email as string,
        message: data.message as string,
        phone: data.phone as string,
      }),
    );
  }

  public reset(): void {
    this.form.reset();
  }

  /* prettier-ignore */
  private issueTypeSelectItemsSource(): Observable<SelectItem<PublicIssueType>[]> {
    return this.issueTypeProvider.selectItems();
  }

  /* prettier-ignore */
  private issueComplaintTypeSelectItemsSource(): Observable<SelectItem<PublicIssueComplaintType>[]> {
    return this.issueComplaintTypeProvider.selectItems();
  }

  private buildForm(): PublicIssueFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      recaptcha: this.builder.nonNullable.control<boolean>(false, [Validators.required, Validators.requiredTrue]),
      type: this.builder.control<Nillable<PublicIssueType>>(null, [Validators.required]),
      dignity: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicIssueForm.DignityMaxLength)]),
      email: this.builder.control<Nillable<string>>(null, [Validators.required, EmailValidator.email]),
      phone: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicIssueForm.PhoneMaxLength)]),
      message: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicIssueForm.MessageMaxLength)]),
      acceptRules: this.builder.nonNullable.control<boolean>(false, [Validators.required, Validators.requiredTrue]),
      complaint: this.builder.group({
        reasons: this.builder.control<Nillable<PublicIssueComplaintType[]>>(null, [Validators.required, ArrayValidator.min(1)]),
        fileIds: this.builder.control<Nillable<Nillable<Guid[]>>>(null, [ArrayValidator.max(5)]),
        orderNumber: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicIssueComplaintForm.OrderNumberMaxLength)]),
      })
    })
  }

  private prepareComplaintFormGroupAvailabilitySource(): Observable<boolean> {
    const { type, complaint } = this.form.controls;

    return type.valueChanges.pipe(
      startWith(type.value),
      map((type) => type === PublicIssueType.Complaint),
      tap((isComplaint) =>
        isComplaint ? complaint.enable() : complaint.disable(),
      ),
    );
  }
}
